import * as React from 'react';
import { useState, useCallback, useEffect, useRef } from 'react';
import { css } from '@emotion/core';
import Card from '../../layout/card/Card';
import { Str } from '../../localization/translationKeys';
import { FLEX_COLUMN } from '../../layout/flexBox';
import { COLORS } from '../../globalStyle/colors';
import { GoalAnswer } from './models';
import LocalTextArea from '../../localization/LocalTextArea';
import { FIELDSET_DEFAULT } from '../../layout/fieldSetStyle';
import { useDispatch } from 'react-redux';
import { useLoading } from '../../core/api/hooks';
import { ActionType } from '../../core/redux/actionType';
import { setGoalAnswerAction } from './setGoalAnswerAction';
import { useProjIds } from '../../navigation/hooks';


const SAVE_DEBOUNCE_MS = 700;


const styles = {
  root:     [
    FLEX_COLUMN,
    css`
      padding: 0;
      margin-bottom: 20px;
      background-color: transparent;
    `,
  ],
  textArea: css`
    width: 100%;
    padding: 10px;
    color: #474747;
    box-sizing: border-box;
    border: none;
    border-radius: 8px;
    background-color: ${COLORS.WHITE};
  `,
};


type Props = {
  className?: string;
  goalAnswer: GoalAnswer;
};


/**
 * Answer: renders a goal's answer in an always-editable text area. Edits are auto-saved on a debounce while typing and
 * immediately on blur.
 */
const Answer: React.FC<Props> = props => {
  const dispatch = useDispatch();
  const projIds  = useProjIds();

  const initial            = props.goalAnswer.questionAnswer || '';
  const [answer, setAnswer] = useState<string>(initial);

  const loading = useLoading(
    ActionType.SetGoalAnswerRequest,
    `${props.goalAnswer.projectId}-${props.goalAnswer.questionId}`,
  );

  // Refs mirror state so stable callbacks read fresh values without re-binding every keystroke.
  const answerRef     = useRef<string>(answer);
  const loadingRef    = useRef<boolean>(loading);
  const wasLoadingRef = useRef<boolean>(loading);
  // The value most recently dispatched. Skip no-op saves; detect divergence during in-flight saves.
  const lastSavedRef  = useRef<string>(initial);
  const timeoutRef    = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => { answerRef.current = answer; },   [answer]);
  useEffect(() => { loadingRef.current = loading; }, [loading]);

  const trySave = useCallback(
    () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      if (loadingRef.current) {
        // Defer: the loading-transition effect will retry once this save completes.
        return;
      }

      const value = answerRef.current;
      if (value === lastSavedRef.current) {
        return;
      }

      lastSavedRef.current = value;
      dispatch(setGoalAnswerAction(
        props.goalAnswer.projectId,
        projIds.participantId,
        props.goalAnswer.questionId,
        value,
      ));
    },
    [dispatch, projIds.participantId, props.goalAnswer.projectId, props.goalAnswer.questionId],
  );

  // Reconcile prop -> local state only when the user has no pending edits. Without this gate, the redux update from our
  // own save would clobber a fresh keystroke.
  useEffect(
    () => {
      const propValue = props.goalAnswer.questionAnswer || '';
      if (answerRef.current === lastSavedRef.current && propValue !== answerRef.current) {
        lastSavedRef.current = propValue;
        setAnswer(propValue);
      }
    },
    [props.goalAnswer.questionAnswer],
  );

  // When a save completes, if the user typed more during the in-flight window, save again.
  useEffect(
    () => {
      if (wasLoadingRef.current && !loading && answerRef.current !== lastSavedRef.current) {
        trySave();
      }
      wasLoadingRef.current = loading;
    },
    [loading, trySave],
  );

  // Flush any pending debounced edit on unmount.
  useEffect(
    () => () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      if (answerRef.current !== lastSavedRef.current && !loadingRef.current) {
        const value = answerRef.current;
        lastSavedRef.current = value;
        dispatch(setGoalAnswerAction(
          props.goalAnswer.projectId,
          projIds.participantId,
          props.goalAnswer.questionId,
          value,
        ));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const onChange = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      setAnswer(event.target.value);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(trySave, SAVE_DEBOUNCE_MS);
    },
    [trySave],
  );

  const onBlur = useCallback(() => trySave(), [trySave]);

  return (
    <Card css={styles.root} className={props.className}>
      <fieldset css={FIELDSET_DEFAULT}>
        <LocalTextArea
          name='answer'
          value={answer}
          css={styles.textArea}
          rows={4}
          placeholderTranslation={Str.ResponseNotAdded}
          onChange={onChange}
          onBlur={onBlur}
        />
      </fieldset>
    </Card>
  );
};

export default Answer;
